import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeDtoStoreProduct, DoppeHideableValue} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    doppeDtoProductGetMediaItems,
    doppeDtoStoreProductPriceDataConvertToActionPrice,
    DoppeExternalProductProvider,
    doppeExternalProductProviderResolveBusinessEntityReference,
    doppeHideableValueCreateHidden,
    doppeHideableValueGetValue,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../client-server-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {arrayRemoveNullValues, asArray, immerProduce, RichTextContent} from '@wix/devzai-utils-common';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeActionCTAType,
    DoppeActionCTATypeLinkAutoSource,
    DoppeActionCTATypeLinkOpenType,
    doppeActionMultipleCTAButtonCreate
} from '../../client-server-common/types/doppe-action-cta-button';
import {autoValueCreate} from '@wix/devzai-utils-common';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';

export namespace DoppeActionTypeStoreProduct {

    export interface BaseActionSettings extends
        DoppeActionMediaViewSettings {
        price: DoppeActionPrice
        productTitle: string;
        description: RichTextContent;
        productImage: WixMediaResource[] | null;
        couponCode: DoppeHideableValue<string>;
        ribbon: string;
        productProvider: DoppeExternalProductProvider | null;
        productProviderId: string | null;
    }

    export interface ActionSettings extends
        BaseActionSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }
}

export const doppeActionTypeStoreProduct = doppeActionTypeDefineMetadata<DoppeActionTypeStoreProduct.ActionSettings>({
    id: 'io.bymo.action/store-product',
    resolveActionSettings: action => {

        const {
            ctaButtons,
            mainMediaViewSettings,
            mainMediaViewLayout,
            ...restSettings
        } = action.settings;

        return {
            productTitle: (''),
            description: (''),
            productImage: null,
            couponCode: doppeHideableValueCreateHidden('SALE30'),
            ribbon: ('SALE'),
            productProvider: DoppeExternalProductProvider.Hopp,
            productProviderId: null,
            price: ({
                actualPrice: 100,
                currencyCode: 'USD',
            }),
            ...doppeActionGetMediaViewDefaultSettingsMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
                ctaButtons: ctaButtons,
            }, false),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            productTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            productImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues(asArray(doppeHideableValueGetVisibleValue(actionSettings.productImage, []) ?? []));
    },
    resolveMainMedia: actionSettings => {
        const mediaItems = arrayRemoveNullValues(asArray(doppeHideableValueGetVisibleValue(actionSettings.productImage, []) ?? []));
        return mediaItems[0] ?? null;
    },
    resolveMainLink: (actionSettings, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            htmlSiteInfo: htmlSiteInfo,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
        }) ?? null;
    },
    resolveCouponData: actionSettings => {
        return {
            showCouponCode: doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.couponCode, ''),
            couponCode: doppeHideableValueGetValue(actionSettings.couponCode),
            couponDescription: '',
            couponTitle: doppeHideableValueGetVisibleValue(actionSettings.productTitle, ''),
        }
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.productProvider !== null && actionSettings.productProviderId !== null ?
                doppeExternalProductProviderResolveBusinessEntityReference(
                    actionSettings.productProvider,
                    actionSettings.productProviderId
                ) :
                null
        ])
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.productImage) {
                const images = actionSettings.productImage ?? [];
                actionSettings.productImage = images.map(image => {
                    return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                })
            }
        })
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
});

export function doppeActionTypeStoreProductResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeStoreProduct.BaseActionSettings,
    storeProduct: DoppeDtoStoreProduct
): DoppeActionTypeStoreProduct.BaseActionSettings {

    const productImages = doppeDtoProductGetMediaItems(storeProduct);

    return {
        ...actionSettings,
        productImage: productImages ?? actionSettings.productImage,
        price: doppeDtoStoreProductPriceDataConvertToActionPrice(storeProduct.price) ?? actionSettings.price,
        productTitle: (storeProduct.name ?? actionSettings.productTitle),
        description: (storeProduct.descriptionPlainText ?? actionSettings.description),
        ribbon: storeProduct.ribbon ?? actionSettings.ribbon,
    }
}

export function convertDoppeWixProductToStoreProductSettings(options: {
    storeProduct: DoppeDtoStoreProduct, productSettings?: DoppeActionTypeStoreProduct.ActionSettings
}) {

    const {
        storeProduct,
        productSettings = {},
    } = options;

    const productImages = doppeDtoProductGetMediaItems(storeProduct)

    const productTitle = storeProduct?.name;

    return {
        couponCode: doppeHideableValueCreateHidden(''),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
            ctaButtons: [doppeActionMultipleCTAButtonCreate({
                text: '',
                isRequired: true,
                settings: {
                    type: DoppeActionCTAType.Link,
                    url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
                    openType: DoppeActionCTATypeLinkOpenType.SameTab
                }
            })],
        }, false),
        ...productSettings,
        ...doppeActionGetMediaViewDefaultSettingsMigration(productSettings),
        ...doppeActionContentSectionSettingsDefaultValuesMigration(productSettings),
        price: (doppeDtoStoreProductPriceDataConvertToActionPrice(storeProduct.price)),
        ribbon: (storeProduct.ribbon ?? ''),
        productImage: (productImages),
        productTitle: (productTitle ?? ''),
        description: (storeProduct.descriptionPlainText),
        productProvider: DoppeExternalProductProvider.Hopp,
        productProviderId: storeProduct.id,
    } satisfies DoppeActionTypeStoreProduct.ActionSettings
}